import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import DayOffRequestModal from '../schedule/DayOffRequestModal'
import FindReplacementModal from '../schedule/FindReplacementModal'
import BlockedDatesCalendar from '../schedule/BlockedDatesCalendar'
import { notifyWorker, getOwnerIds } from '../../lib/notifyWorker'

var DOW_LABELS = ['', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
var STATUS_COLORS = {
  pending: { bg: 'rgba(180,140,60,0.12)', border: 'rgba(180,140,60,0.3)', fg: '#b48c3c', label: 'PENDING' },
  approved: { bg: 'rgba(74,140,106,0.12)', border: 'rgba(74,140,106,0.3)', fg: '#4a8c6a', label: 'APPROVED' },
  denied: { bg: 'rgba(224,92,92,0.12)', border: 'rgba(224,92,92,0.3)', fg: '#e05c5c', label: 'DENIED' },
}

function fmtDate(s) {
  if (!s) return ''
  return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function LaborerSettingsPage() {
  var { user, profile } = useAuth()
  var [section, setSection] = useState('days_off')
  var [dayOffReqs, setDayOffReqs] = useState([])
  var [availability, setAvailability] = useState([])
  var [showRequest, setShowRequest] = useState(false)
  var [showFindReplacement, setShowFindReplacement] = useState(null)

  var [form, setForm] = useState({ full_name: '', phone: '', email: '', season_start: '', season_end: '', is_student: false, availability_notes: '', new_password: '', confirm_password: '' })
  var [saveStatus, setSaveStatus] = useState('')

  var load = useCallback(async function() {
    if (!user?.id) return
    var [dRes, aRes] = await Promise.all([
      supabase.from('day_off_requests').select('*').eq('user_id', user.id).order('request_date', { ascending: false }),
      supabase.from('worker_availability').select('*').eq('user_id', user.id).order('day_of_week'),
    ])
    setDayOffReqs(dRes.data || [])
    setAvailability(aRes.data || [])
  }, [user?.id])

  useEffect(function() { load() }, [load])

  useEffect(function() {
    if (profile) {
      setForm(function(f) {
        return Object.assign({}, f, {
          full_name: profile.full_name || '',
          phone: profile.phone || '',
          email: profile.email || '',
          season_start: profile.season_start || '',
          season_end: profile.season_end || '',
          is_student: !!profile.is_student,
          availability_notes: profile.availability_notes || '',
        })
      })
    }
  }, [profile])

  // === DAY OFF REQUEST HANDLERS ===
  var submitDayOff = async function(payload) {
    var { data, error } = await supabase.from('day_off_requests').insert({
      user_id: user.id, request_date: payload.date, reason: payload.reason || null, status: 'pending',
    }).select().single()
    if (error) { alert('Error: ' + error.message); return }
    var ownerIds = await getOwnerIds()
    await Promise.all(ownerIds.map(function(uid) {
      return notifyWorker({ userId: uid, type: 'day_off_requested', title: 'Day off request',
        message: (profile?.full_name || 'A worker') + ' \u2014 ' + fmtDate(payload.date), link: '/schedule?request=' + data.id, relatedId: data.id })
    }))
    setShowRequest(false)
    setShowFindReplacement(data)
    load()
  }

  // === AVAILABILITY HANDLERS ===
  var setDayAvail = async function(dow, kind) {
    var patch = { user_id: user.id, day_of_week: dow, available: kind !== 'no', available_after: kind === 'after' ? '15:00' : null }
    await supabase.from('worker_availability').upsert(patch, { onConflict: 'user_id,day_of_week' })
    load()
  }

  var setAfterTime = async function(dow, time) {
    await supabase.from('worker_availability').update({ available_after: time }).eq('user_id', user.id).eq('day_of_week', dow)
    load()
  }

  var availOf = function(dow) {
    var row = availability.find(function(a) { return a.day_of_week === dow })
    if (!row) return { kind: 'yes', after: null }
    if (!row.available) return { kind: 'no', after: null }
    if (row.available_after) return { kind: 'after', after: row.available_after }
    return { kind: 'yes', after: null }
  }

  // === PROFILE SAVE ===
  var saveProfile = async function() {
    setSaveStatus('saving')
    var patch = { full_name: form.full_name, phone: form.phone, email: form.email }
    // Only include optional fields if they may exist on the schema
    if ('availability_notes' in (profile || {})) patch.availability_notes = form.availability_notes
    var { error } = await supabase.from('profiles').update(patch).eq('id', user.id)
    setSaveStatus(error ? 'error' : 'saved')
    setTimeout(function() { setSaveStatus('') }, 3000)
  }

  var saveSeason = async function() {
    setSaveStatus('saving')
    var patch = { season_start: form.season_start || null, season_end: form.season_end || null }
    if ('is_student' in (profile || {})) patch.is_student = !!form.is_student
    var { error } = await supabase.from('profiles').update(patch).eq('id', user.id)
    setSaveStatus(error ? 'error' : 'saved')
    setTimeout(function() { setSaveStatus('') }, 3000)
  }

  var savePassword = async function() {
    if (form.new_password !== form.confirm_password) { alert('Passwords do not match'); return }
    if (form.new_password.length < 8) { alert('Password must be at least 8 characters'); return }
    setSaveStatus('saving')
    var { error } = await supabase.auth.updateUser({ password: form.new_password })
    if (error) { alert(error.message); setSaveStatus('error') }
    else { setSaveStatus('saved'); setForm(function(f) { return Object.assign({}, f, { new_password: '', confirm_password: '' }) }) }
    setTimeout(function() { setSaveStatus('') }, 3000)
  }

  var set = function(k, v) { setForm(function(f) { return Object.assign({}, f, { [k]: v }) }) }

  // Season status
  var today = new Date().toISOString().slice(0, 10)
  var inSeason = form.season_start && form.season_end && today >= form.season_start && today <= form.season_end

  return (
    <div style={st.wrap}>
      <div style={st.header}>
        <div style={st.title}>SETTINGS</div>
        <div style={st.subtitle}>{profile?.full_name || 'Me'}</div>
      </div>

      {/* Section nav */}
      <div style={st.sectionNav}>
        {[
          { k: 'days_off', l: 'DAYS OFF' },
          { k: 'season', l: 'SEASON' },
          { k: 'blocked', l: 'BLOCKED' },
          { k: 'profile', l: 'PROFILE' },
        ].map(function(s) {
          return <button key={s.k} onClick={function() { setSection(s.k) }} style={Object.assign({}, st.sectionBtn, section === s.k ? st.sectionBtnActive : {})}>{s.l}</button>
        })}
      </div>

      {saveStatus === 'saved' && <div style={st.savedBanner}>{String.fromCodePoint(0x2713)} Saved</div>}
      {saveStatus === 'error' && <div style={st.errorBanner}>Save failed</div>}

      {/* ======== DAY OFF REQUESTS ======== */}
      {section === 'days_off' && (
        <div>
          <div style={st.card}>
            <div style={st.cardTitle}>REQUEST A DAY OFF</div>
            <div style={st.cardBody}>
              <p style={st.muted}>Need a specific day off? Submit a request and the owner will approve or deny.</p>
              <button onClick={function() { setShowRequest(true) }} style={st.primaryBtn}>+ NEW DAY OFF REQUEST</button>
            </div>
          </div>

          <div style={st.secTitle}>YOUR REQUESTS</div>
          {dayOffReqs.length === 0 ? (
            <div style={st.empty}>No requests yet.</div>
          ) : dayOffReqs.map(function(r) {
            var c = STATUS_COLORS[r.status] || STATUS_COLORS.pending
            return (
              <div key={r.id} style={Object.assign({}, st.reqCard, { borderColor: c.border, backgroundColor: c.bg })}>
                <div style={st.reqTop}>
                  <div>
                    <div style={st.reqDate}>{fmtDate(r.request_date)}</div>
                    {r.reason && <div style={st.reqReason}>{r.reason}</div>}
                  </div>
                  <span style={Object.assign({}, st.reqStatus, { color: c.fg })}>{c.label}</span>
                </div>
                {r.status === 'pending' && (
                  <button onClick={function() { setShowFindReplacement(r) }} style={st.findBtn}>FIND REPLACEMENT</button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ======== SEASON ======== */}
      {section === 'season' && (
        <div>
          <div style={st.card}>
            <div style={st.cardTitle}>SEASON STATUS</div>
            <div style={st.cardBody}>
              <div style={Object.assign({}, st.statusPill, inSeason ? st.statusGreen : st.statusGrey)}>
                {inSeason ? 'IN SEASON ' + String.fromCodePoint(0x2713) : 'OFF SEASON'}
              </div>
            </div>
          </div>

          <div style={st.card}>
            <div style={st.cardTitle}>WHEN CAN YOU START / STOP?</div>
            <div style={st.cardBody}>
              <div style={st.field}>
                <label style={st.label}>SEASON START</label>
                <input type="date" value={form.season_start} onChange={function(e) { set('season_start', e.target.value) }} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>SEASON END</label>
                <input type="date" value={form.season_end} onChange={function(e) { set('season_end', e.target.value) }} style={st.input} />
              </div>
              <label style={st.checkboxRow}>
                <input type="checkbox" checked={form.is_student} onChange={function(e) { set('is_student', e.target.checked) }} style={st.checkbox} />
                <span>I'm a student — set my after-school availability</span>
              </label>
              <button onClick={saveSeason} style={st.primaryBtn}>SAVE SEASON</button>
            </div>
          </div>

          <div style={st.card}>
            <div style={st.cardTitle}>WEEKLY AVAILABILITY</div>
            <div style={st.cardBody}>
              <p style={st.muted}>{form.is_student ? 'Set your after-school availability for each day.' : 'Set your recurring weekly availability.'}</p>
              {[1, 2, 3, 4, 5, 6].map(function(dow) {
                var av = availOf(dow)
                return (
                  <div key={dow} style={st.availRow}>
                    <span style={st.availDay}>{DOW_LABELS[dow]}</span>
                    <select value={av.kind} onChange={function(e) { setDayAvail(dow, e.target.value) }} style={st.select}>
                      <option value="yes">Available All Day</option>
                      <option value="after">After School</option>
                      <option value="no">Not Available</option>
                    </select>
                    {av.kind === 'after' && (
                      <input type="time" value={av.after || '15:00'} onChange={function(e) { setAfterTime(dow, e.target.value) }} style={st.timeInput} />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* ======== BLOCKED DATES ======== */}
      {section === 'blocked' && (
        <div>
          <div style={st.card}>
            <div style={st.cardTitle}>BLOCK DATES</div>
            <div style={st.cardBody}>
              <p style={st.muted}>Tap any open day in the next 3 months to request it off. Sundays are already off.</p>
              <BlockedDatesCalendar />
            </div>
          </div>
        </div>
      )}

      {/* ======== PROFILE ======== */}
      {section === 'profile' && (
        <div>
          <div style={st.card}>
            <div style={st.cardTitle}>CONTACT INFO</div>
            <div style={st.cardBody}>
              <div style={st.field}>
                <label style={st.label}>FULL NAME</label>
                <input value={form.full_name} onChange={function(e) { set('full_name', e.target.value) }} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>PHONE</label>
                <input value={form.phone} onChange={function(e) { set('phone', e.target.value) }} style={st.input} type="tel" />
              </div>
              <div style={st.field}>
                <label style={st.label}>EMAIL</label>
                <input value={form.email} onChange={function(e) { set('email', e.target.value) }} style={st.input} type="email" />
              </div>
              <div style={st.field}>
                <label style={st.label}>AVAILABILITY NOTES</label>
                <textarea value={form.availability_notes} onChange={function(e) { set('availability_notes', e.target.value) }} style={Object.assign({}, st.input, { minHeight: '60px', resize: 'vertical' })} placeholder="e.g. Done with school June 12" />
              </div>
              <button onClick={saveProfile} style={st.primaryBtn}>SAVE PROFILE</button>
            </div>
          </div>

          <div style={st.card}>
            <div style={st.cardTitle}>CHANGE PASSWORD</div>
            <div style={st.cardBody}>
              <div style={st.field}>
                <label style={st.label}>NEW PASSWORD</label>
                <input value={form.new_password} onChange={function(e) { set('new_password', e.target.value) }} style={st.input} type="password" placeholder="Min 8 characters" />
              </div>
              <div style={st.field}>
                <label style={st.label}>CONFIRM</label>
                <input value={form.confirm_password} onChange={function(e) { set('confirm_password', e.target.value) }} style={st.input} type="password" />
              </div>
              <button onClick={savePassword} disabled={!form.new_password} style={st.primaryBtn}>UPDATE PASSWORD</button>
            </div>
          </div>
        </div>
      )}

      {showRequest && <DayOffRequestModal onClose={function() { setShowRequest(false) }} onSubmit={submitDayOff} />}
      {showFindReplacement && <FindReplacementModal request={showFindReplacement} onClose={function() { setShowFindReplacement(null); load() }} />}
    </div>
  )
}

var st = {
  wrap: { fontFamily: "'Barlow', sans-serif", maxWidth: '560px', paddingBottom: '40px' },
  header: { marginBottom: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },

  sectionNav: { display: 'flex', gap: '4px', marginBottom: '20px', overflowX: 'auto' },
  sectionBtn: { flex: 1, minWidth: '72px', padding: '10px 6px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer', whiteSpace: 'nowrap' },
  sectionBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },

  savedBanner: { padding: '8px 14px', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '4px', color: '#4a8c6a', fontSize: '13px', marginBottom: '14px' },
  errorBanner: { padding: '8px 14px', backgroundColor: 'rgba(224,92,92,0.15)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '4px', color: '#e05c5c', fontSize: '13px', marginBottom: '14px' },

  card: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', marginBottom: '14px', overflow: 'hidden' },
  cardTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', padding: '12px 16px', backgroundColor: 'rgba(180,140,60,0.05)', borderBottom: '1px solid rgba(180,140,60,0.15)' },
  cardBody: { padding: '16px' },
  muted: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: 0, marginBottom: '12px', lineHeight: '1.5' },

  primaryBtn: { width: '100%', padding: '12px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '4px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },

  secTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px', marginTop: '18px' },
  empty: { padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '13px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.06)', borderRadius: '4px' },

  reqCard: { padding: '12px 14px', border: '1px solid', borderRadius: '4px', marginBottom: '6px' },
  reqTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' },
  reqDate: { fontSize: '14px', fontWeight: 600, color: '#ffffff' },
  reqReason: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },
  reqStatus: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', whiteSpace: 'nowrap' },
  findBtn: { marginTop: '8px', padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid rgba(180,140,60,0.35)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },

  field: { marginBottom: '12px' },
  label: { display: 'block', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', fontSize: '14px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', marginBottom: '10px' },
  checkbox: { width: '18px', height: '18px', accentColor: '#b48c3c', cursor: 'pointer' },

  statusPill: { display: 'inline-block', padding: '8px 16px', borderRadius: '20px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px' },
  statusGreen: { backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.4)', color: '#4a8c6a' },
  statusGrey: { backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)' },

  availRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  availDay: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.6)', minWidth: '38px' },
  select: { flex: 1, padding: '8px 10px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', outline: 'none' },
  timeInput: { padding: '8px', backgroundColor: 'rgba(180,140,60,0.08)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '3px', color: '#b48c3c', fontSize: '13px', outline: 'none', width: '100px' },
}
